import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLang, tr } from "../contexts/LangContext";
import { getLowPlacesThreshold, isLowPlaces } from "../lib/lowPlaces";
import { useCart } from "../contexts/CartContext";
import api from "../lib/api";
import { ShareButtons } from "../components/ShareButtons";
import { ArrowLeft, Plus, Minus, Check, Calendar as CalIcon, Users, AlertCircle } from "lucide-react";

export default function Product() {
  const { id } = useParams();
  const { t, lang, content } = useLang();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  // Extended participant fields (stages only)
  const [participant, setParticipant] = useState({
    first_name: "", last_name: "", birthdate: "",
    email: "", phone: "", address: "",
    weight: "", height: "", boat: "monoplace", experience: "",
  });
  const updateP = (k, v) => setParticipant((cur) => ({ ...cur, [k]: v }));
  const [added, setAdded] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setSize((r.data.sizes && r.data.sizes[0]) || "");
      setColor((r.data.colors && r.data.colors[0]) || "");
    }).catch(() => {});
  }, [id]);

  if (!p) return <div className="pt-32 pb-20 text-center text-slate-500" data-testid="product-loading">{t.common.loading}</div>;

  // Distinguish bookable types — stages get the full detailed form, events the lightweight one.
  const isStage = p.type === "stage";
  const isEvent = p.type === "event";
  const isBookable = isStage || isEvent;
  const placesLeft = isBookable ? (p.max_places || 0) - (p.places_taken || 0) : 0;
  const isFull = isBookable && (p.stage_status === "full" || placesLeft <= 0);
  const isClosed = isBookable && p.stage_status === "closed";
  const isLow = isBookable && isLowPlaces({ places: p.max_places, taken: p.places_taken, isFull, isClosed, threshold: getLowPlacesThreshold(content) });
  const disabled = isBookable ? (isFull || isClosed) : p.stock <= 0;

  const add = () => {
    let extra = {};
    if (isStage) {
      extra = {
        participant_first_name: participant.first_name.trim(),
        participant_last_name:  participant.last_name.trim(),
        participant_birthdate:  participant.birthdate,
        participant_email:      participant.email.trim(),
        participant_phone:      participant.phone.trim(),
        participant_address:    participant.address.trim(),
        participant_weight:     participant.weight,
        participant_height:     participant.height,
        participant_boat:       participant.boat,
        participant_experience: participant.experience.trim(),
        // Composite name kept for backward compat with cart-line key & email templates
        participant_name: `${participant.first_name} ${participant.last_name}`.trim(),
        participant_notes: participant.experience.trim(),
      };
    } else if (isEvent) {
      // Events use the simpler legacy form (just a name + optional notes)
      extra = {
        participant_name: participant.first_name.trim()
          || `${participant.first_name} ${participant.last_name}`.trim(),
        participant_notes: participant.experience.trim(),
      };
    }
    addItem({ ...p, ...extra }, qty, isBookable ? "" : size, isBookable ? "" : color);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  // Stage form valid when all required fields are filled.
  // Event form valid as soon as a participant name is provided.
  const stageFormValid = (() => {
    if (isStage) {
      return (
        participant.first_name.trim() &&
        participant.last_name.trim() &&
        participant.birthdate &&
        participant.email.trim() &&
        participant.phone.trim() &&
        participant.weight &&
        participant.height &&
        participant.boat
      );
    }
    if (isEvent) return !!participant.first_name.trim();
    return true;
  })();

  return (
    <div className="pt-28 pb-24 bg-white" data-testid="product-page">
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12">
        <Link to="/shop" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-navy mb-8" data-testid="back-to-shop">
          <ArrowLeft className="w-4 h-4" /> {t.shop.backToShop}
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
          <div className="aspect-square overflow-hidden bg-slate-100 relative">
            {p.image ? (
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy/5 to-club-yellow/10">
                <span className="text-sm uppercase tracking-widest text-slate-400">{p.name}</span>
              </div>
            )}
            {isBookable && (
              <div className={`absolute top-4 left-4 text-xs uppercase tracking-widest px-3 py-1.5 font-semibold ${isFull ? "bg-club-red text-white" : isClosed ? "bg-slate-500 text-white" : isLow ? "bg-club-red text-white animate-pulse" : "bg-club-yellow text-navy"}`} data-testid={isLow ? "product-last-places-badge" : undefined}>
                {isFull ? t.shop.stageFull : isClosed ? t.shop.stageClosed : isLow ? t.stages.lastPlaces : t.shop.stageOpen}
              </div>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-club-yellow-dark mb-3 font-semibold">{t.shop.categories[p.category] || p.category}</div>
            <h1 className="font-serif text-4xl lg:text-5xl font-light text-navy">{p.name}</h1>
            <div className="divider-gold mt-5" />
            <p className="text-slate-600 mt-6 leading-relaxed">{p.description}</p>

            <div className="font-serif text-4xl text-navy mt-8">€ {p.price.toFixed(2)}</div>

            {isBookable ? (
              <div className="mt-4 space-y-3">
                {p.start_date && (
                  <div className="flex items-center gap-3 text-sm">
                    <CalIcon className="w-4 h-4 text-club-yellow-dark" />
                    <span className="text-navy">
                      {new Date(p.start_date).toLocaleDateString(lang === "en" ? "en-GB" : lang, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                      {p.end_date && p.end_date !== p.start_date && ` → ${new Date(p.end_date).toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "numeric", month: "long" })}`}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-sm">
                  <Users className="w-4 h-4 text-club-yellow-dark" />
                  <span className={isFull || isLow ? "text-club-red font-semibold" : "text-navy"}>
                    {isFull ? t.shop.stageFull : `${placesLeft} ${t.shop.placesLeft} / ${p.max_places}`}
                  </span>
                </div>
                {isFull && !isClosed && <WaitlistForm productId={p.id} t={t} lang={lang} />}
                <div className="pt-2">
                  <ShareButtons
                    lang={lang}
                    label={t.stages.share}
                    title={p.name}
                    url={`${window.location.origin}/${lang}/shop/${p.id}`}
                    testid="share-product"
                  />
                </div>
              </div>
            ) : (
              <div className={`text-xs uppercase tracking-widest mt-1 ${p.stock > 0 ? "text-green-700" : "text-red-600"}`}>
                {p.stock > 0 ? `${t.shop.inStock} (${p.stock})` : t.shop.outOfStock}
              </div>
            )}

            {!isBookable && p.sizes && p.sizes.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t.shop.size}</div>
                <div className="flex flex-wrap gap-2">
                  {p.sizes.map((s) => (
                    <button key={s} onClick={() => setSize(s)} className={`px-4 py-2 text-sm border transition-colors ${size === s ? "border-navy bg-navy text-white" : "border-slate-300 text-slate-600 hover:border-navy"}`} data-testid={`size-${s}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!isBookable && p.colors && p.colors.length > 0 && (
              <div className="mt-5">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t.shop.color}</div>
                <div className="flex flex-wrap gap-2">
                  {p.colors.map((c) => (
                    <button key={c} onClick={() => setColor(c)} className={`px-4 py-2 text-sm border transition-colors ${color === c ? "border-navy bg-navy text-white" : "border-slate-300 text-slate-600 hover:border-navy"}`} data-testid={`color-${c}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isStage && !disabled && (
              <div className="mt-6 space-y-3 bg-sand p-5 border border-slate-200" data-testid="stage-booking-form">
                <div className="text-xs uppercase tracking-widest text-club-red font-semibold">{t.shop.participantInfo}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <StageField label={t.shop.firstName} required value={participant.first_name} onChange={(v) => updateP("first_name", v)} testid="stage-fld-first_name" />
                  <StageField label={t.shop.lastName}  required value={participant.last_name}  onChange={(v) => updateP("last_name", v)}  testid="stage-fld-last_name"  />
                  <StageField label={t.shop.birthdate} type="date" required value={participant.birthdate} onChange={(v) => updateP("birthdate", v)} testid="stage-fld-birthdate" />
                  <StageField label={t.shop.phone}     type="tel"   required value={participant.phone} onChange={(v) => updateP("phone", v)} testid="stage-fld-phone" />
                  <StageField label={t.shop.email}     type="email" required value={participant.email} onChange={(v) => updateP("email", v)} testid="stage-fld-email" />
                  <StageField label={t.shop.address}   value={participant.address} onChange={(v) => updateP("address", v)} testid="stage-fld-address" />
                  <StageField label={t.shop.weight}    type="number" required value={participant.weight} onChange={(v) => updateP("weight", v)} testid="stage-fld-weight" suffix="kg" />
                  <StageField label={t.shop.height}    type="number" required value={participant.height} onChange={(v) => updateP("height", v)} testid="stage-fld-height" suffix="cm" />
                  <div className="sm:col-span-2">
                    <label className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1.5">{t.shop.boatType} *</label>
                    <div className="flex gap-2" data-testid="stage-fld-boat">
                      {[
                        { v: "monoplace", l: t.shop.boatMono },
                        { v: "biplace",   l: t.shop.boatBi },
                      ].map(({ v, l }) => (
                        <button key={v} type="button" onClick={() => updateP("boat", v)}
                          className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${participant.boat === v ? "border-navy bg-navy text-white" : "border-slate-300 text-slate-600 hover:border-navy"}`}
                          data-testid={`stage-boat-${v}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1.5">{t.shop.experience}</label>
                    <textarea
                      placeholder={t.shop.experiencePlaceholder}
                      value={participant.experience}
                      onChange={(e) => updateP("experience", e.target.value)}
                      rows={3}
                      className="w-full border border-slate-300 outline-none focus:border-navy px-3 py-2 resize-none bg-white text-sm"
                      data-testid="stage-fld-experience"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">* {t.shop.requiredFields}</p>
              </div>
            )}

            {isEvent && !disabled && (
              <div className="mt-6 space-y-3 bg-sand p-5 border border-slate-200" data-testid="event-booking-form">
                <div className="text-xs uppercase tracking-widest text-club-red font-semibold">{t.shop.participantInfo}</div>
                <StageField
                  label={t.shop.participantName} required
                  value={participant.first_name}
                  onChange={(v) => updateP("first_name", v)}
                  testid="event-fld-name"
                />
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1.5">{t.shop.participantNotes}</label>
                  <textarea
                    placeholder={t.shop.participantNotes}
                    value={participant.experience}
                    onChange={(e) => updateP("experience", e.target.value)}
                    rows={2}
                    className="w-full border border-slate-300 outline-none focus:border-navy px-3 py-2 resize-none bg-white text-sm"
                    data-testid="event-fld-notes"
                  />
                </div>
              </div>
            )}

            {!disabled && (
              <div className="mt-6 flex items-center gap-3">
                <div className="text-xs uppercase tracking-widest text-slate-500 mr-2">{t.shop.quantity}</div>
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 border border-slate-300 hover:border-navy flex items-center justify-center" data-testid="qty-dec"><Minus className="w-3 h-3" /></button>
                <span className="w-10 text-center font-serif text-xl text-navy" data-testid="qty-value">{qty}</span>
                <button
                  onClick={() => setQty(qty + 1)}
                  disabled={isBookable && qty >= placesLeft}
                  className="w-9 h-9 border border-slate-300 hover:border-navy flex items-center justify-center disabled:opacity-40"
                  data-testid="qty-inc"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            )}

            {disabled && (
              <div className="mt-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-800 p-4 text-sm" data-testid="product-unavailable">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {isFull ? t.shop.stageFull : isClosed ? t.shop.stageClosed : t.shop.outOfStock}
              </div>
            )}

            <button
              onClick={add}
              disabled={disabled || !stageFormValid}
              className="mt-8 w-full bg-navy text-white py-4 text-xs uppercase tracking-[0.25em] hover:bg-club-red transition-colors disabled:opacity-50"
              data-testid="add-to-cart"
            >
              {added ? <span className="inline-flex items-center gap-2 justify-center"><Check className="w-4 h-4" />{tr("Ajouté")}</span>
                : isBookable ? t.shop.bookStage : t.shop.addToCart}
            </button>
            <button onClick={() => navigate("/cart")} className="mt-3 w-full border border-navy text-navy py-3 text-xs uppercase tracking-[0.25em] hover:bg-navy hover:text-white transition-colors" data-testid="go-to-cart">
              {t.nav.cart} →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StageField({ label, type = "text", value, onChange, required, testid, suffix }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1.5">
        {label}{required && " *"}
      </label>
      <div className="relative">
        <input
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-slate-300 outline-none focus:border-navy px-3 py-2 bg-white text-sm pr-10"
          data-testid={testid}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span>}
      </div>
    </div>
  );
}

function WaitlistForm({ productId, t, lang }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    api.get(`/stages/waitlist-count/${productId}`).then((r) => setCount(r.data?.count || 0)).catch(() => {});
  }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/stages/waitlist", { product_id: productId, name, email, lang });
      setDone(true);
      setCount((c) => c + 1);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mt-6 flex items-start gap-2 bg-green-50 border border-green-200 text-green-800 text-sm p-4" data-testid="waitlist-success">
        <Check className="w-4 h-4 mt-0.5 shrink-0" /> {t.shop.waitlistSuccess}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 bg-sand border border-slate-200 p-6" data-testid="waitlist-form">
      <div className="text-[10px] uppercase tracking-[0.25em] text-club-red font-semibold mb-1">{t.shop.waitlistTitle}</div>
      <p className="text-sm text-slate-600 mb-1">{t.shop.waitlistText}</p>
      {count > 0 && (
        <p className="text-xs text-club-red italic mb-3" data-testid="product-waitlist-count">
          {count === 1 ? t.stages.waitlistOne : t.stages.waitlistMany.replace("{n}", count)}
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 mt-3">
        <input required value={name} onChange={(e) => setName(e.target.value)} placeholder={t.shop.name}
          className="border border-slate-300 focus:border-navy outline-none px-3 py-2 bg-white text-sm" data-testid="waitlist-name" />
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.shop.email}
          className="border border-slate-300 focus:border-navy outline-none px-3 py-2 bg-white text-sm" data-testid="waitlist-email" />
      </div>
      {err && <div className="text-xs text-red-600 mb-3" data-testid="waitlist-error">{err}</div>}
      <button type="submit" disabled={busy}
        className="bg-navy text-white px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red transition-colors disabled:opacity-50"
        data-testid="waitlist-submit">
        {t.shop.waitlistBtn}
      </button>
    </form>
  );
}
